import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const TermsPage = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <>
            <SEO 
                title="Terms of Service | AircoreDynamics"
                description="Read AircoreDynamics terms of service, user agreement, and usage guidelines. Understand your rights and responsibilities."
                keywords="terms of service, user agreement, terms and conditions, usage policy"
                image="https://aircoredynamics.com/og-image.png"
                url="/terms"
                type="website"
            />
            <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                transition={{ duration: 0.6 }}
                className="container mx-auto px-6 py-12 max-w-4xl"
            >
            {/* Header */}
            <div className="text-center mb-12">
                <DocumentTextIcon className="h-16 w-16 mx-auto mb-4 text-light-primary dark:text-dark-primary" />
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                    Terms of <span className="gradient-text">Service</span>
                </h1>
                <p className="text-light-text/70 dark:text-dark-text/70">
                    Last Updated: October 29, 2025
                </p>
            </div>

            {/* Content */}
            <div className="card p-8 space-y-8">
                
                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        1. Acceptance of Terms
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        By accessing and using AircoreDynamics ("the Platform"), you accept and agree to be bound by the 
                        terms and conditions of this agreement. If you do not agree to these terms, please do not use our Platform.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        2. Use of Service
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed mb-4">
                        AircoreDynamics provides an educational platform for learning about aviation, aerospace, and aerodynamics. 
                        You agree to:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-light-text/80 dark:text-dark-text/80 ml-4">
                        <li>Use the Platform only for lawful purposes</li>
                        <li>Not attempt to gain unauthorized access to any part of the Platform</li>
                        <li>Not interfere with or disrupt the Platform's operation</li>
                        <li>Not use the Platform to transmit harmful or malicious code</li>
                        <li>Respect the intellectual property rights of all content</li>
                        <li>Provide accurate and truthful information when creating an account</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        3. User Accounts
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        To access certain features, you must create an account. You are responsible for:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-light-text/80 dark:text-dark-text/80 ml-4 mt-4">
                        <li>Maintaining the confidentiality of your account credentials</li>
                        <li>All activities that occur under your account</li>
                        <li>Notifying us immediately of any unauthorized access</li>
                        <li>Ensuring your account information is accurate and up-to-date</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        4. Course Enrollment and Access
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        When you enroll in a course, you gain access to course materials for the duration specified. 
                        Course content is for personal, non-commercial use only. You may not:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-light-text/80 dark:text-dark-text/80 ml-4 mt-4">
                        <li>Share your account access with others</li>
                        <li>Reproduce, distribute, or sell course materials</li>
                        <li>Record or screenshot course content without permission</li>
                        <li>Use course materials for commercial purposes</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        5. Certificates
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        Upon successful completion of a course, you may receive a certificate. These certificates:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-light-text/80 dark:text-dark-text/80 ml-4 mt-4">
                        <li>Are digital proof of course completion</li>
                        <li>Can be verified using a unique certificate ID</li>
                        <li>May be revoked if course completion was achieved through fraudulent means</li>
                        <li>Should not be misrepresented as professional licenses or accreditations</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        6. Intellectual Property
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        All content on the Platform, including but not limited to text, graphics, logos, videos, and software, 
                        is the property of AircoreDynamics or its content suppliers and is protected by international copyright 
                        and intellectual property laws. Unauthorized use of any content may violate copyright, trademark, and 
                        other laws.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        7. User-Generated Content
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        You may be able to post comments, questions, or other content. By posting content, you grant us a 
                        worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display such content 
                        in connection with the Platform. You represent that you own or have the necessary rights to post 
                        such content.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        8. Disclaimer of Warranties
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        The Platform is provided "as is" and "as available" without warranties of any kind, either express 
                        or implied. We do not guarantee that:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-light-text/80 dark:text-dark-text/80 ml-4 mt-4">
                        <li>The Platform will be uninterrupted or error-free</li>
                        <li>Defects will be corrected</li>
                        <li>The Platform is free of viruses or other harmful components</li>
                        <li>The results of using the Platform will meet your requirements</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        9. Limitation of Liability
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        To the maximum extent permitted by law, AircoreDynamics shall not be liable for any indirect, 
                        incidental, special, consequential, or punitive damages, or any loss of profits or revenues, 
                        whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible 
                        losses resulting from your use of the Platform.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        10. Modifications to Service
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        We reserve the right to modify or discontinue, temporarily or permanently, the Platform (or any 
                        part thereof) with or without notice. You agree that we shall not be liable to you or any third 
                        party for any modification, suspension, or discontinuance of the Platform.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        11. Termination
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        We may terminate or suspend your account and access to the Platform immediately, without prior 
                        notice or liability, for any reason, including if you breach these Terms. Upon termination, your 
                        right to use the Platform will immediately cease.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        12. Governing Law
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        These Terms shall be governed by and construed in accordance with the laws of Worldwide, without 
                        regard to its conflict of law provisions. Any disputes arising from these Terms or use of the 
                        Platform shall be subject to the exclusive jurisdiction of the courts of Worldwide.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        13. Changes to Terms
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        We reserve the right to modify these Terms at any time. We will notify users of any material 
                        changes by posting the new Terms on the Platform and updating the "Last Updated" date. Your 
                        continued use of the Platform after such changes constitutes acceptance of the new Terms.
                    </p>
                </section>

                <section>
                    <h2 className="text-2xl font-bold mb-4 text-light-primary dark:text-dark-primary">
                        14. Contact Information
                    </h2>
                    <p className="text-light-text/80 dark:text-dark-text/80 leading-relaxed">
                        If you have any questions about these Terms, please contact us at:
                    </p>
                    <div className="mt-4 p-4 bg-light-bg dark:bg-dark-bg rounded-lg">
                        <p className="text-light-text dark:text-dark-text">
                            <strong>Email:</strong> legal@aircoredynamics.com<br />
                            <strong>Address:</strong> Worldwide
                        </p>
                    </div>
                </section>

            </div>

            {/* Back Button */}
            <div className="text-center mt-8">
                <Link 
                    to="/" 
                    className="btn-secondary inline-block"
                >
                    Back to Home
                </Link>
            </div>
        </motion.div>
        </>
    );
};

export default TermsPage;
